import { PopoverClose } from "@radix-ui/react-popover";
import { X } from "lucide-react";
import React from "react";
import type { Components } from "react-markdown";
import { Button } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  makeFlexibleSectionRegex,
  makeWordBoundaryRegex,
} from "./escapeRegExp";

export type HighlightableTerm = { term: string; definition: string };
export type Term = { term: string; definition: string };
export type SectionLink = {
  sectionId: string;
  sectionName: string;
  firstPageId: string;
};
export type BatesLink = {
  batesNumber: string;
  pageId: string;
};

export const unwrapGfmTablesInFences = (content: string): string => {
  if (!content) return content;

  const lines = content.split("\n");
  const out: string[] = [];

  let inFence = false;
  let fenceMarker = "";
  let blockStartIdx = -1;
  let language = "";

  const isGfmTableBlock = (blockLines: string[]) => {
    const trimmed = blockLines.map((l) => l.trim());
    let headerIdx = -1;
    for (let i = 0; i < trimmed.length; i++) {
      const line = trimmed[i];
      if (/\|/.test(line)) {
        headerIdx = i;
        break;
      }
    }
    if (headerIdx === -1) return false;
    if (headerIdx + 1 >= trimmed.length) return false;
    const sep = trimmed[headerIdx + 1];
    if (!/^\|?\s*:?-{3,}\s*(\|\s*:?-{3,}\s*)+\|?$/.test(sep)) return false;
    return true;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);
    if (fenceMatch) {
      const marker = fenceMatch[2];
      if (!inFence) {
        inFence = true;
        fenceMarker = marker;
        blockStartIdx = out.length;
        language = (fenceMatch[3] || "").toLowerCase();
        out.push(line);
      } else if (marker === fenceMarker) {
        const blockLines = out.slice(blockStartIdx + 1);
        const shouldUnwrap =
          isGfmTableBlock(blockLines) ||
          language === "table" ||
          language === "markdown";

        if (shouldUnwrap) {
          const keptBefore = out.slice(0, blockStartIdx);
          out.length = 0;
          out.push(...keptBefore, ...blockLines);
        } else {
          out.push(line);
        }

        inFence = false;
        fenceMarker = "";
        blockStartIdx = -1;
        language = "";
      } else {
        out.push(line);
      }
      continue;
    }

    out.push(line);
  }

  return out.join("\n");
};

export const unwrapAllCodeBlocks = (content: string): string => {
  if (!content) return content;

  const lines = content.split("\n");
  const out: string[] = [];

  let inFence = false;
  let fenceMarker = "";
  let blockStartIdx = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);

    if (fenceMatch) {
      const marker = fenceMatch[2];
      if (!inFence) {
        inFence = true;
        fenceMarker = marker;
        blockStartIdx = out.length;
      } else if (marker === fenceMarker) {
        const blockLines = out.slice(blockStartIdx);
        const keptBefore = out.slice(0, blockStartIdx);
        out.length = 0;
        out.push(...keptBefore, ...blockLines);

        inFence = false;
        fenceMarker = "";
        blockStartIdx = -1;
      } else {
        out.push(line);
      }
      continue;
    }

    out.push(line);
  }

  return out.join("\n");
};
export const TERMS_PROSE_CLASS =
  "prose prose-sm dark:prose-invert font-sans normal-case";

/**
 * Shared React Markdown components to render abbr terms with popovers
 * and handle abbr tags inside inline code while preserving code styling.
 */
export function getTermsMarkdownComponents(terms: Term[]): Components {
  type CustomSpanProps = React.HTMLAttributes<HTMLElement> & {
    "data-term-id"?: string;
  };

  const Abbr = ({ ...props }: CustomSpanProps) => {
    const termId = props["data-term-id"];
    if (termId !== undefined) {
      const term = terms[Number(termId)];
      if (term) {
        return (
          <Popover>
            <PopoverTrigger asChild>
              <abbr
                {...props}
                className={`cursor-pointer underline decoration-dotted${props.className ? ` ${props.className}` : ""}`}
              />
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              sideOffset={10}
              className={TERMS_PROSE_CLASS}
            >
              <PopoverClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="inline-flex items-center justify-center border-0 absolute hover:bg-transparent right-0 top-0"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverClose>
              <span className="block text-lg font-semibold m-0 p-0">
                {term.term}
              </span>
              <span className="block text-sm font-normal leading-[20px] mb-0">
                {term.definition}
              </span>
            </PopoverContent>
          </Popover>
        );
      }
    }
    return <abbr {...props} />;
  };

  const Code = ({
    children,
    className,
    ...props
  }: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
    className?: string;
    children?: React.ReactNode;
  }) => {
    const toArray = React.Children.toArray(children);
    const rawText = toArray
      .map((c) => (typeof c === "string" ? c : ""))
      .join("");

    const isBlockCode =
      (className && className.includes("language-")) || rawText.includes("\n");
    if (isBlockCode) {
      return (
        <code {...props} className={className}>
          {children}
        </code>
      );
    }

    const decodedText = rawText
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">");

    if (!decodedText.includes("<abbr")) {
      return (
        <code {...props} className={className}>
          {children}
        </code>
      );
    }

    const parts: React.ReactNode[] = [];
    const regex = /<abbr\s+data-term-id="(\d+)"\s*>\s*([^<]+?)\s*<\/abbr>/gi;

    let lastIndex = 0;
    for (
      let match = regex.exec(decodedText);
      match;
      match = regex.exec(decodedText)
    ) {
      if (match.index > lastIndex) {
        parts.push(decodedText.slice(lastIndex, match.index));
      }
      const id = match[1];
      const label = match[2];
      parts.push(
        <Abbr key={`${match.index}-${id}`} data-term-id={id}>
          {label}
        </Abbr>
      );
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < decodedText.length) {
      parts.push(decodedText.slice(lastIndex));
    }

    const cleanedParts = parts.map((seg) =>
      typeof seg === "string"
        ? seg
            .replace(/&lt;\/?abbr[^&gt;]*&gt;/gi, "")
            .replace(/<\/?abbr[^>]*>/gi, "")
        : seg
    );

    return (
      <code {...props} className={className}>
        {cleanedParts}
      </code>
    );
  };

  const PageReference = ({
    children,
    className,
    ...props
  }: React.DetailedHTMLProps<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    HTMLAnchorElement
  > & {
    className?: string;
    children?: React.ReactNode;
  }) => {
    const isPageRef =
      className?.includes("page-reference") || "data-page-id" in props;

    if (isPageRef) {
      const text = typeof children === "string" ? children : String(children);
      const numberOnly = text.replace(/[\[\]]/g, "");

      // Check if this is a knowledge base source with unknown IDs
      const pageId = (props as any)["data-page-id"];
      const orgId = (props as any)["data-organization-id"];
      const isKnowledgeBaseSource =
        pageId === "unknown" || orgId === "unknown" || !pageId || !orgId;

      return (
        <a
          {...props}
          className={`page-reference inline-flex items-center justify-center text-[10px] px-2 py-1 rounded-full no-underline transition-colors mx-0.5 ${
            isKnowledgeBaseSource
              ? "bg-muted text-muted-foreground/60 cursor-not-allowed"
              : "bg-muted text-muted-foreground hover:bg-accent cursor-pointer"
          }`}
          title={
            isKnowledgeBaseSource
              ? "This source is from the knowledge base and cannot be viewed"
              : undefined
          }
        >
          {numberOnly}
        </a>
      );
    }
    return (
      <a {...props} className={className}>
        {children}
      </a>
    );
  };

  return {
    abbr: Abbr as any,
    code: Code as any,
    a: PageReference as any,
  };
}
export function highlightTermsInMarkdown(
  content: string,
  terms: Array<HighlightableTerm>
): string {
  if (!content || !terms.length) return content;

  const lines = content.split("\n");
  let inFence = false;
  let fenceMarker = "";
  const processedLines: string[] = [];

  const replaceInText = (text: string) => {
    if (!text) return text;

    type Seg = {
      type: "code" | "text" | "section-link" | "bates-link";
      value: string;
    };
    const parts: Seg[] = [];
    let i = 0;
    let buffer = "";

    const flushText = () => {
      if (buffer) {
        parts.push({ type: "text", value: buffer });
        buffer = "";
      }
    };

    while (i < text.length) {
      // Check for section-link anchor tags - skip highlighting inside them
      const sectionLinkPrefix = '<a class="section-link"';
      if (text.startsWith(sectionLinkPrefix, i)) {
        const closeTag = "</a>";
        const closeIdx = text.indexOf(closeTag, i);
        if (closeIdx !== -1) {
          flushText();
          const sectionLinkChunk = text.slice(i, closeIdx + closeTag.length);
          parts.push({ type: "section-link", value: sectionLinkChunk });
          i = closeIdx + closeTag.length;
          continue;
        }
      }

      const batesLinkPrefix = '<a class="bates-link"';
      if (text.startsWith(batesLinkPrefix, i)) {
        const closeTag = "</a>";
        const closeIdx = text.indexOf(closeTag, i);
        if (closeIdx !== -1) {
          flushText();
          const batesLinkChunk = text.slice(i, closeIdx + closeTag.length);
          parts.push({ type: "bates-link", value: batesLinkChunk });
          i = closeIdx + closeTag.length;
          continue;
        }
      }

      if (text[i] === "`") {
        let j = i;
        while (j < text.length && text[j] === "`") j++;
        const tickCount = j - i;

        let k = j;
        let end = -1;
        while (k < text.length) {
          if (text[k] === "`") {
            let m = k;
            while (m < text.length && text[m] === "`") m++;
            const count = m - k;
            if (count === tickCount) {
              end = m;
              break;
            }
            k = m;
          } else {
            k++;
          }
        }

        if (end !== -1) {
          flushText();
          const codeChunk = text.slice(i, end);
          parts.push({ type: "code", value: codeChunk });
          i = end;
          continue;
        } else {
          buffer += text.slice(i, j);
          i = j;
          continue;
        }
      }

      buffer += text[i];
      i++;
    }
    flushText();

    const result = parts
      .map((seg) => {
        if (
          seg.type === "code" ||
          seg.type === "section-link" ||
          seg.type === "bates-link"
        )
          return seg.value;
        return terms.reduce((acc, term, index) => {
          const regex = makeWordBoundaryRegex(term.term, "gi");
          return acc.replace(
            regex,
            (match) =>
              `<abbr data-term-id="${index}">${match
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;")}</abbr>`
          );
        }, seg.value);
      })
      .join("");

    return result;
  };

  for (let k = 0; k < lines.length; k++) {
    const line = lines[k];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);
    if (fenceMatch) {
      if (!inFence) {
        inFence = true;
        fenceMarker = fenceMatch[2];
        processedLines.push(line);
        continue;
      } else if (inFence && fenceMatch[2] === fenceMarker) {
        inFence = false;
        fenceMarker = "";
        processedLines.push(line);
        continue;
      }
    }

    if (inFence) {
      processedLines.push(line);
    } else {
      processedLines.push(replaceInText(line));
    }
  }

  return processedLines.join("\n");
}

export function highlightSectionsInMarkdown(
  content: string,
  sections: Array<SectionLink>
): string {
  if (!content || !sections.length) return content;

  const lines = content.split("\n");
  let inFence = false;
  let fenceMarker = "";
  const processedLines: string[] = [];

  const replaceInText = (text: string) => {
    if (!text) return text;

    type Seg = { type: "code" | "text"; value: string };
    const parts: Seg[] = [];
    let i = 0;
    let buffer = "";

    const flushText = () => {
      if (buffer) {
        parts.push({ type: "text", value: buffer });
        buffer = "";
      }
    };

    while (i < text.length) {
      if (text[i] === "`") {
        let j = i;
        while (j < text.length && text[j] === "`") j++;
        const tickCount = j - i;

        let k = j;
        let end = -1;
        while (k < text.length) {
          if (text[k] === "`") {
            let m = k;
            while (m < text.length && text[m] === "`") m++;
            const count = m - k;
            if (count === tickCount) {
              end = m;
              break;
            }
            k = m;
          } else {
            k++;
          }
        }

        if (end !== -1) {
          flushText();
          const codeChunk = text.slice(i, end);
          parts.push({ type: "code", value: codeChunk });
          i = end;
          continue;
        } else {
          buffer += text.slice(i, j);
          i = j;
          continue;
        }
      }

      buffer += text[i];
      i++;
    }
    flushText();

    const sortedSections = [...sections].sort(
      (a, b) => b.sectionName.length - a.sectionName.length
    );

    // Helper to apply section replacement while protecting already-inserted section links
    const applySectionReplacement = (
      text: string,
      section: SectionLink
    ): string => {
      const sectionLinkPattern =
        /<a class="(?:section-link|bates-link)"[^>]*>.*?<\/a>/g;
      const protectedParts: Array<{
        type: "protected" | "text";
        value: string;
      }> = [];
      let lastIndex = 0;
      let match;

      while ((match = sectionLinkPattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
          protectedParts.push({
            type: "text",
            value: text.slice(lastIndex, match.index),
          });
        }
        protectedParts.push({ type: "protected", value: match[0] });
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        protectedParts.push({ type: "text", value: text.slice(lastIndex) });
      }

      if (protectedParts.length === 0) {
        protectedParts.push({ type: "text", value: text });
      }

      const regex = makeFlexibleSectionRegex(section.sectionName, "gi");
      const escapedSectionId = section.sectionId.replace(/"/g, "&quot;");
      const escapedFirstPageId = section.firstPageId.replace(/"/g, "&quot;");
      return protectedParts
        .map((part) => {
          if (part.type === "protected") return part.value;
          return part.value.replace(
            regex,
            (m) =>
              `<a class="section-link" data-section-id="${escapedSectionId}" data-first-page-id="${escapedFirstPageId}">${m
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;")}</a>`
          );
        })
        .join("");
    };

    const result = parts
      .map((seg) => {
        if (seg.type === "code") return seg.value;
        return sortedSections.reduce(
          (acc, section) => applySectionReplacement(acc, section),
          seg.value
        );
      })
      .join("");

    return result;
  };

  for (let k = 0; k < lines.length; k++) {
    const line = lines[k];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);
    if (fenceMatch) {
      if (!inFence) {
        inFence = true;
        fenceMarker = fenceMatch[2];
        processedLines.push(line);
        continue;
      } else if (inFence && fenceMatch[2] === fenceMarker) {
        inFence = false;
        fenceMarker = "";
        processedLines.push(line);
        continue;
      }
    }

    if (inFence) {
      processedLines.push(line);
    } else {
      processedLines.push(replaceInText(line));
    }
  }

  return processedLines.join("\n");
}

export function getSectionLinkMarkdownComponents(
  _sections: SectionLink[],
  onSectionClick?: (sectionId: string, firstPageId: string) => void
): Components {
  type CustomAnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    "data-section-id"?: string;
    "data-first-page-id"?: string;
    className?: string;
    children?: React.ReactNode;
  };

  const SectionLinkComponent = ({ children, ...props }: CustomAnchorProps) => {
    const sectionId = props["data-section-id"];
    const firstPageId = props["data-first-page-id"];
    const isSectionLink = props.className?.includes("section-link");

    if (isSectionLink && sectionId && firstPageId) {
      return (
        <a
          {...props}
          className="cursor-pointer underline decoration-dotted"
          onClick={(e) => {
            e.preventDefault();
            onSectionClick?.(sectionId, firstPageId);
          }}
        >
          {children}
        </a>
      );
    }

    return (
      <a {...props} className={props.className}>
        {children}
      </a>
    );
  };

  return {
    a: SectionLinkComponent as any,
  };
}

export function highlightBatesInMarkdown(
  content: string,
  batesLinks: Array<BatesLink>
): string {
  if (!content || !batesLinks.length) return content;

  const batesMap = new Map<string, string>();
  for (const link of batesLinks) {
    batesMap.set(link.batesNumber.toLowerCase(), link.pageId);
  }

  const batesPattern = /\[Bates:\s*([^\]]+)\]/gi;

  const lines = content.split("\n");
  let inFence = false;
  let fenceMarker = "";
  const processedLines: string[] = [];

  for (let k = 0; k < lines.length; k++) {
    const line = lines[k];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);
    if (fenceMatch) {
      if (!inFence) {
        inFence = true;
        fenceMarker = fenceMatch[2];
        processedLines.push(line);
        continue;
      } else if (inFence && fenceMatch[2] === fenceMarker) {
        inFence = false;
        fenceMarker = "";
        processedLines.push(line);
        continue;
      }
    }

    if (inFence) {
      processedLines.push(line);
    } else {
      processedLines.push(replaceInText(line));
    }
  }

  return processedLines.join("\n");

  function replaceInText(text: string): string {
    if (!text) return text;

    type Seg = { type: "code" | "text"; value: string };
    const parts: Seg[] = [];
    let i = 0;
    let buffer = "";

    const flushText = () => {
      if (buffer) {
        parts.push({ type: "text", value: buffer });
        buffer = "";
      }
    };

    while (i < text.length) {
      if (text[i] === "`") {
        let j = i;
        while (j < text.length && text[j] === "`") j++;
        const tickCount = j - i;

        let k = j;
        let end = -1;
        while (k < text.length) {
          if (text[k] === "`") {
            let m = k;
            while (m < text.length && text[m] === "`") m++;
            const count = m - k;
            if (count === tickCount) {
              end = m;
              break;
            }
            k = m;
          } else {
            k++;
          }
        }

        if (end !== -1) {
          flushText();
          parts.push({ type: "code", value: text.slice(i, end) });
          i = end;
          continue;
        } else {
          buffer += text.slice(i, j);
          i = j;
          continue;
        }
      }

      buffer += text[i];
      i++;
    }
    flushText();

    return parts
      .map((seg) => {
        if (seg.type === "code") return seg.value;
        return seg.value.replace(
          batesPattern,
          (fullMatch, batesNum: string) => {
            const trimmed = batesNum.trim();
            const pageId = batesMap.get(trimmed.toLowerCase());
            if (!pageId) return fullMatch;
            const escapedPageId = pageId.replace(/"/g, "&quot;");
            return `<a class="bates-link" data-bates-page-id="${escapedPageId}">${fullMatch.replace(/"/g, "&quot;").replace(/'/g, "&#39;")}</a>`;
          }
        );
      })
      .join("");
  }
}

export function getTermsAndSectionsMarkdownComponents(
  terms: Term[],
  sections: SectionLink[],
  onSectionClick?: (sectionId: string, firstPageId: string) => void,
  onBatesClick?: (pageId: string) => void
): Components {
  const termComponents = getTermsMarkdownComponents(terms);
  const sectionComponents = getSectionLinkMarkdownComponents(
    sections,
    onSectionClick
  );

  type CustomAnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    "data-section-id"?: string;
    "data-first-page-id"?: string;
    "data-bates-page-id"?: string;
    className?: string;
    children?: React.ReactNode;
  };

  const CombinedAnchor = ({ children, ...props }: CustomAnchorProps) => {
    const isSectionLink = props.className?.includes("section-link");
    const isBatesLink = props.className?.includes("bates-link");
    const isPageRef =
      props.className?.includes("page-reference") || "data-page-id" in props;

    if (isSectionLink) {
      const SectionLink = sectionComponents.a as React.ComponentType<any>;
      return <SectionLink {...props}>{children}</SectionLink>;
    }

    if (isBatesLink) {
      const batesPageId = props["data-bates-page-id"];
      if (batesPageId && onBatesClick) {
        return (
          <a
            {...props}
            className="cursor-pointer underline decoration-dotted"
            onClick={(e) => {
              e.preventDefault();
              onBatesClick(batesPageId);
            }}
          >
            {children}
          </a>
        );
      }
      return (
        <a {...props} className={props.className}>
          {children}
        </a>
      );
    }

    if (isPageRef) {
      const PageRef = termComponents.a as React.ComponentType<any>;
      return <PageRef {...props}>{children}</PageRef>;
    }

    return (
      <a {...props} className={props.className}>
        {children}
      </a>
    );
  };

  return {
    ...termComponents,
    a: CombinedAnchor as any,
  };
}

export function highlightTermsAndSectionsInMarkdown(
  content: string,
  terms: Array<HighlightableTerm>,
  sections: Array<SectionLink>,
  batesLinks?: Array<BatesLink>
): string {
  let result = content;
  if (batesLinks?.length) {
    result = highlightBatesInMarkdown(result, batesLinks);
  }
  if (sections.length) {
    result = highlightSectionsInMarkdown(result, sections);
  }
  if (terms.length) {
    result = highlightTermsInMarkdown(result, terms);
  }
  return result;
}
